import { baseApi, providesList } from '@/shared/api'
import { DEFAULT_PAGE_SIZE } from '@/shared/lib'
import type {
  AddCartItemBody,
  AddedCartItem,
  CartPage,
  CartQuery,
  CartSection,
  CartSummary,
  CartWriteResult,
  UpdateCartItemArgs,
} from '../model/types'

/** Корзина меняется целиком: поменяли строку — поехали и итоги, и счётчик в шапке. */
const CART_INVALIDATION = [
  { type: 'Cart' as const, id: 'LIST' },
  { type: 'Cart' as const, id: 'SUMMARY' },
]

export const cartApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /** Страница строк одной вкладки цен; `summary` — итоги раздела целиком. */
    getCart: build.query<CartPage, CartQuery>({
      query: ({ page = 1, pageSize = DEFAULT_PAGE_SIZE, ...rest }) => ({
        url: '/cart',
        params: { page, pageSize, ...rest },
      }),
      providesTags: (res) => providesList(res?.items, 'Cart'),
    }),

    /**
     * Итоги без страницы строк: счётчик в шапке, итоги раздела, пустой ли раздел на оформлении.
     * Отдельной ручки итогов у бэка нет — `summary` приходит в каждом ответе `GET /cart` и не
     * зависит от вкладки, поэтому строка запрашивается одна, а в кеш ложится только сводка.
     * Итоги считаются по разделу целиком: `section` (по умолчанию `own`) — ключ записи кеша.
     */
    getCartSummary: build.query<CartSummary, CartSection | void>({
      query: (section) => ({
        url: '/cart',
        params: { page: 1, pageSize: 1, ...(section && section !== 'own' ? { section } : {}) },
      }),
      transformResponse: (res: CartPage) => res.summary,
      providesTags: [{ type: 'Cart', id: 'SUMMARY' }],
    }),

    /** Цену, имя и остаток берёт сервер; тот же товар на той же вкладке не задваивается. */
    addCartItem: build.mutation<AddedCartItem, AddCartItemBody>({
      query: (body) => ({ url: '/cart/items', method: 'POST', body }),
      invalidatesTags: CART_INVALIDATION,
    }),

    updateCartItemQuantity: build.mutation<CartWriteResult, UpdateCartItemArgs>({
      query: ({ itemId, quantity }) => ({
        url: '/cart/items',
        method: 'PATCH',
        body: { items: [{ id: itemId, quantity }] },
      }),
      invalidatesTags: CART_INVALIDATION,
      /*
       * Счётчик количества должен отзываться мгновенно, поэтому в кеше правится
       * ровно одно поле — quantity. Итоги не трогаются намеренно: их считает сервер
       * (правило 11), их принесёт рефетч по инвалидации.
       */
      async onQueryStarted({ itemId, quantity }, { dispatch, getState, queryFulfilled }) {
        const patches = cartApi.util.selectCachedArgsForQuery(getState(), 'getCart').map((args) =>
          dispatch(
            cartApi.util.updateQueryData('getCart', args, (draft) => {
              const item = draft.items.find((row) => row.id === itemId)
              if (item) item.quantity = quantity
            }),
          ),
        )

        try {
          await queryFulfilled
        } catch {
          patches.forEach((patch) => patch.undo())
        }
      },
    }),

    removeCartItem: build.mutation<CartWriteResult, string>({
      query: (itemId) => ({ url: '/cart/items', method: 'DELETE', body: { ids: [itemId] } }),
      invalidatesTags: CART_INVALIDATION,
    }),
  }),
})

export const {
  useGetCartQuery,
  useGetCartSummaryQuery,
  useAddCartItemMutation,
  useUpdateCartItemQuantityMutation,
  useRemoveCartItemMutation,
} = cartApi
